import React, { useEffect, useRef, useState, useMemo } from 'react'
import { browserLanguageToLocaleMap, localesCountryTextMap } from 'i18n'
import { Button, MetaSeo, JsonLd } from 'components/atoms'
import { Header, Footer, GoogleReCaptchaWrapper, CountryLightbox } from 'components/organisms'
import useUtente from '@hooks/useUtente'
import useTrans from '@hooks/useTrans'
import useLocale from '@hooks/useLocale'
import { LANGUAGES } from '@utils/settings'
import { isToolbarVisible } from '@utils/index'
import { useRouter } from 'next/router'
import styles from './Page.module.sass'
import useCookie from '@hooks/useCookie'

interface Props {
  pagina: any // PaginaNode
  item?: any
  children: React.ReactNode
  className?: string
  contentClassName?: string
  transparentHeader?: boolean
  recaptchaRequired?: boolean
}

const Page = (props: Props) => {
  const {
    pagina,
    item = {},
    children = null,
    className = '',
    contentClassName = '',
    transparentHeader = false,
    recaptchaRequired = false,
  } = props

  const router = useRouter()
  const [scrollY, setScrollY] = useState(0)
  const [countryItem, setCountryItem] = useState(null)
  const [openCountryLightbox, setOpenCountryLightbox] = useState(false)

  const mainRef = useRef(undefined)
  const { utente } = useUtente()
  const showToolbar = isToolbarVisible(utente)
  const t = useTrans()
  const { clusterLangs } = useLocale()
  const [cookieExists, setCookie] = useCookie('currentCountry', '')

  const setCookiesCountry = (type: string, toLocale: string) => {
    if (type === 'new') {
      setCookie(toLocale)
    }
    if (type === 'current') {
      setCookie(router.locale)
    }
  }

  const handleScroll = () => {
    if (window.scrollY > 0) document.body.classList.add('w-scrolled')
    else document.body.classList.remove('w-scrolled')
    if (window.scrollY < scrollY) document.body.classList.add('w-scrollup')
    else document.body.classList.remove('w-scrollup')
    setScrollY(window.scrollY)
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [scrollY])

  const [Toolbar, setToolbar] = useState<React.JSX.Element>()

  useEffect(() => {
    // carico il componente solo se l'utente ha i permessi
    const loadToolbar = async () => {
      const ToolbarComponent = (await import('components/organisms/Toolbar/Toolbar')).default
      setToolbar(<ToolbarComponent page={pagina} item={item} />)
    }

    if (showToolbar) {
      loadToolbar()
    }
  }, [showToolbar])

  useEffect(() => {
    if (cookieExists && cookieExists === router.locale) return

    if (navigator?.language) {
      const currentLocale = browserLanguageToLocaleMap[navigator.language] || null

      if (currentLocale && currentLocale !== router.locale) {
        const hrefFinded = pagina?.hreflangs.find((h) => h.locale === currentLocale)?.href
        const countryText = localesCountryTextMap[currentLocale]
        if (!hrefFinded || !countryText) return

        setCountryItem({
          alias: countryText.alias,
          name: countryText.name,
          url: hrefFinded,
          key: currentLocale,
        })
        setOpenCountryLightbox(true)
      }
    }
  }, [router.locale])

  const langs = useMemo(() => {
    return clusterLangs?.map((l) => {
      const hreflang = pagina?.hreflangs?.find((h) => h.locale === l)
      return hreflang
        ? {
            name: LANGUAGES?.[hreflang.locale] || hreflang.locale,
            locale: hreflang.locale,
            url: hreflang.href,
            selected: router.locale === hreflang.locale,
            unoptimized: true,
          }
        : {
            name: LANGUAGES?.[l] || l,
            locale: l,
            url: `/${l}`,
            selected: router.locale === l,
            unoptimized: true,
          }
    })
  }, [pagina])

  return (
    <GoogleReCaptchaWrapper recaptchaRequired={recaptchaRequired}>
      <div className={styles.pageWrap}>
        <Button
          label={t('Vai al contenuto')}
          onClick={() => {
            mainRef?.current?.focus()
          }}
          className={styles.skipToMain}
        />
        {showToolbar && Toolbar}
        <div
          className={`${styles.root} page ${
            transparentHeader ? styles['root--transparentHeader'] : ''
          } ${className}`}
        >
          {pagina?.seo && <MetaSeo seo={pagina.seo} hreflangs={pagina.hreflangs} />}
          <Header
            item={Object.keys(item).length > 0 ? item : pagina}
            langs={langs}
            transparentHeader={transparentHeader}
          />
          <div className={`${styles.content} ${contentClassName}`} ref={mainRef} tabIndex={-1}>
            {children}
          </div>

          <Footer langs={langs} />
          <CountryLightbox
            item={countryItem}
            isOpen={openCountryLightbox}
            setCookies={setCookiesCountry}
            setIsOpen={setOpenCountryLightbox}
          />
        </div>
      </div>
      <JsonLd type="organization" />
      <JsonLd type="search" />
    </GoogleReCaptchaWrapper>
  )
}

export default Page
